<div class="p-8 min-h-screen bg-gray-50">
  <!-- Header -->
  <div class="mb-6 flex items-center justify-between">
    <div>
      <h1 class="text-3xl font-bold text-gray-900">Jobs Management</h1>
      <p class="text-gray-600 mt-1">Manage and track all service jobs</p>
    </div>
    <button
      (click)="openCreateJobModal()"
      class="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium"
    >
      <i class="fas fa-plus mr-2"></i>Create New Job
    </button>
  </div>

  <!-- Filters and Search -->
  <div class="bg-white rounded-lg shadow-sm p-4 mb-6">
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Status Filter</label>
        <select
          [(ngModel)]="statusFilter"
          (change)="onStatusFilterChange()"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          @for (option of statusOptions; track option.value) {
            <option [value]="option.value">{{ option.label }}</option>
          }
        </select>
      </div>
      <div class="md:col-span-2">
        <label class="block text-sm font-medium text-gray-700 mb-2">Search</label>
        <div class="relative">
          <input
            type="text"
            [(ngModel)]="searchTerm"
            (input)="onSearchChange()"
            placeholder="Search by job ID, vehicle, driver, or service type..."
            class="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
        </div>
      </div>
    </div>
  </div>

  <!-- Error Message -->
  @if (error) {
    <div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
      <i class="fas fa-exclamation-circle mr-2"></i>{{ error }}
    </div>
  }

  @if (pushNotificationWarning) {
    <div class="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg mb-6 flex items-start justify-between gap-3">
      <div>
        <i class="fas fa-bell-slash mr-2"></i>
        <span class="font-medium">Driver was not notified by push:</span>
        {{ pushNotificationWarning }}
      </div>
      <button
        type="button"
        (click)="clearPushNotificationWarning()"
        class="shrink-0 text-amber-800 hover:text-amber-950 text-sm underline"
        aria-label="Dismiss notification warning"
      >
        Dismiss
      </button>
    </div>
  }

  <!-- Loading State -->
  @if (loading) {
    <div class="text-center py-12">
      <i class="fas fa-spinner fa-spin text-4xl text-primary"></i>
      <p class="mt-4 text-gray-600">Loading jobs...</p>
    </div>
  }

  <!-- Jobs Table -->
  @if (!loading && filteredJobs.length > 0) {
    <div #jobsTableWrap class="jobs-table-wrap bg-white rounded-lg shadow-sm overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr #jobsTableHeaderRow>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(0)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[0] === 'left'" [title]="jobsTableStickyMode[0] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(0, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[0] === 'left'" [class.text-gray-300]="jobsTableStickyMode[0] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[0] === 'right'" [title]="jobsTableStickyMode[0] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(0, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[0] === 'right'" [class.text-gray-300]="jobsTableStickyMode[0] !== 'right'"></i>
                  </button>
                </span>
                Job ID
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(1)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[1] === 'left'" [title]="jobsTableStickyMode[1] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(1, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[1] === 'left'" [class.text-gray-300]="jobsTableStickyMode[1] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[1] === 'right'" [title]="jobsTableStickyMode[1] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(1, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[1] === 'right'" [class.text-gray-300]="jobsTableStickyMode[1] !== 'right'"></i>
                  </button>
                </span>
                Status
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(2)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[2] === 'left'" [title]="jobsTableStickyMode[2] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(2, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[2] === 'left'" [class.text-gray-300]="jobsTableStickyMode[2] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[2] === 'right'" [title]="jobsTableStickyMode[2] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(2, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[2] === 'right'" [class.text-gray-300]="jobsTableStickyMode[2] !== 'right'"></i>
                  </button>
                </span>
                Call Type
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(3)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[3] === 'left'" [title]="jobsTableStickyMode[3] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(3, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[3] === 'left'" [class.text-gray-300]="jobsTableStickyMode[3] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[3] === 'right'" [title]="jobsTableStickyMode[3] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(3, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[3] === 'right'" [class.text-gray-300]="jobsTableStickyMode[3] !== 'right'"></i>
                  </button>
                </span>
                Vehicle
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(4)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[4] === 'left'" [title]="jobsTableStickyMode[4] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(4, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[4] === 'left'" [class.text-gray-300]="jobsTableStickyMode[4] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[4] === 'right'" [title]="jobsTableStickyMode[4] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(4, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[4] === 'right'" [class.text-gray-300]="jobsTableStickyMode[4] !== 'right'"></i>
                  </button>
                </span>
                Client
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(5)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[5] === 'left'" [title]="jobsTableStickyMode[5] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(5, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[5] === 'left'" [class.text-gray-300]="jobsTableStickyMode[5] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[5] === 'right'" [title]="jobsTableStickyMode[5] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(5, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[5] === 'right'" [class.text-gray-300]="jobsTableStickyMode[5] !== 'right'"></i>
                  </button>
                </span>
                Contact
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(6)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[6] === 'left'" [title]="jobsTableStickyMode[6] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(6, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[6] === 'left'" [class.text-gray-300]="jobsTableStickyMode[6] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[6] === 'right'" [title]="jobsTableStickyMode[6] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(6, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[6] === 'right'" [class.text-gray-300]="jobsTableStickyMode[6] !== 'right'"></i>
                  </button>
                </span>
                Pickup Location
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(7)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[7] === 'left'" [title]="jobsTableStickyMode[7] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(7, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[7] === 'left'" [class.text-gray-300]="jobsTableStickyMode[7] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[7] === 'right'" [title]="jobsTableStickyMode[7] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(7, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[7] === 'right'" [class.text-gray-300]="jobsTableStickyMode[7] !== 'right'"></i>
                  </button>
                </span>
                Destination
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(8)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[8] === 'left'" [title]="jobsTableStickyMode[8] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(8, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[8] === 'left'" [class.text-gray-300]="jobsTableStickyMode[8] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[8] === 'right'" [title]="jobsTableStickyMode[8] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(8, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[8] === 'right'" [class.text-gray-300]="jobsTableStickyMode[8] !== 'right'"></i>
                  </button>
                </span>
                Service Type
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(9)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[9] === 'left'" [title]="jobsTableStickyMode[9] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(9, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[9] === 'left'" [class.text-gray-300]="jobsTableStickyMode[9] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[9] === 'right'" [title]="jobsTableStickyMode[9] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(9, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[9] === 'right'" [class.text-gray-300]="jobsTableStickyMode[9] !== 'right'"></i>
                  </button>
                </span>
                Priority
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(10)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[10] === 'left'" [title]="jobsTableStickyMode[10] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(10, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[10] === 'left'" [class.text-gray-300]="jobsTableStickyMode[10] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[10] === 'right'" [title]="jobsTableStickyMode[10] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(10, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[10] === 'right'" [class.text-gray-300]="jobsTableStickyMode[10] !== 'right'"></i>
                  </button>
                </span>
                Driver
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(11)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[11] === 'left'" [title]="jobsTableStickyMode[11] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(11, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[11] === 'left'" [class.text-gray-300]="jobsTableStickyMode[11] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[11] === 'right'" [title]="jobsTableStickyMode[11] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(11, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[11] === 'right'" [class.text-gray-300]="jobsTableStickyMode[11] !== 'right'"></i>
                  </button>
                </span>
                Cost
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(12)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[12] === 'left'" [title]="jobsTableStickyMode[12] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(12, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[12] === 'left'" [class.text-gray-300]="jobsTableStickyMode[12] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[12] === 'right'" [title]="jobsTableStickyMode[12] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(12, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[12] === 'right'" [class.text-gray-300]="jobsTableStickyMode[12] !== 'right'"></i>
                  </button>
                </span>
                Payment
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(13)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[13] === 'left'" [title]="jobsTableStickyMode[13] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(13, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[13] === 'left'" [class.text-gray-300]="jobsTableStickyMode[13] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[13] === 'right'" [title]="jobsTableStickyMode[13] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(13, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[13] === 'right'" [class.text-gray-300]="jobsTableStickyMode[13] !== 'right'"></i>
                  </button>
                </span>
                Invoice #
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(14)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[14] === 'left'" [title]="jobsTableStickyMode[14] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(14, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[14] === 'left'" [class.text-gray-300]="jobsTableStickyMode[14] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[14] === 'right'" [title]="jobsTableStickyMode[14] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(14, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[14] === 'right'" [class.text-gray-300]="jobsTableStickyMode[14] !== 'right'"></i>
                  </button>
                </span>
                Company
              </span>
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(15)">
              <span class="inline-flex items-center gap-1.5">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[15] === 'left'" [title]="jobsTableStickyMode[15] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(15, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[15] === 'left'" [class.text-gray-300]="jobsTableStickyMode[15] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[15] === 'right'" [title]="jobsTableStickyMode[15] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(15, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[15] === 'right'" [class.text-gray-300]="jobsTableStickyMode[15] !== 'right'"></i>
                  </button>
                </span>
                Created
              </span>
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" [ngStyle]="getStickyThStyle(16)">
              <span class="inline-flex items-center justify-end gap-1.5 w-full">
                <span class="inline-flex items-center gap-0.5 shrink-0" role="group" aria-label="Column pin">
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[16] === 'left'" [title]="jobsTableStickyMode[16] === 'left' ? 'Unpin from left' : 'Pin to left'" (click)="setJobsTableStickySide(16, 'left'); $event.stopPropagation()">
                    <i class="fas fa-chevron-left text-[9px]" [class.text-primary]="jobsTableStickyMode[16] === 'left'" [class.text-gray-300]="jobsTableStickyMode[16] !== 'left'"></i>
                  </button>
                  <button type="button" class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 transition-colors" [attr.aria-pressed]="jobsTableStickyMode[16] === 'right'" [title]="jobsTableStickyMode[16] === 'right' ? 'Unpin from right' : 'Pin to right'" (click)="setJobsTableStickySide(16, 'right'); $event.stopPropagation()">
                    <i class="fas fa-chevron-right text-[9px]" [class.text-primary]="jobsTableStickyMode[16] === 'right'" [class.text-gray-300]="jobsTableStickyMode[16] !== 'right'"></i>
                  </button>
                </span>
                Actions
              </span>
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          @for (job of filteredJobs; track job.id) {
            <tr class="group hover:bg-gray-50">
              <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(0)">#{{ job.id }}</td>
              <td class="px-4 py-3 whitespace-nowrap bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(1)">
                <span class="px-2 py-1 text-xs font-semibold rounded-full {{ getStatusBadgeClass(job.status) }}">
                  {{ formatJobStatus(job.status) }}
                </span>
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-600 bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(2)">{{ job.callType || 'N/A' }}</td>
              <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(3)">
                <div class="max-w-xs">
                  <div class="font-medium">{{ getVehicleDisplay(job.vehicle) }}</div>
                  @if (job.licensePlate) {
                    <div class="text-xs text-gray-500">{{ job.licensePlate }} {{ job.licenseState || '' }}</div>
                  }
                </div>
              </td>
              
              <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-600 bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(4)">
                <div class="max-w-xs">
                  <div>{{ job.clientName || 'N/A' }}</div>
                  @if (job.clientEmail) {
                    <div class="text-xs text-gray-500">{{ job.clientEmail }}</div>
                  }
                </div>
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-600 bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(5)">
                <div class="max-w-xs">
                  @if (job.contactName) {
                    <div>{{ job.contactName }}</div>
                  }
                  @if (job.contactPhoneNumber) {
                    <div class="text-xs text-gray-500">{{ job.contactPhoneNumber }}</div>
                  }
                  @if (!job.contactName && !job.contactPhoneNumber) {
                    <span class="text-gray-400">N/A</span>
                  }
                </div>
              </td>
              <td class="px-4 py-3 text-sm text-gray-600 max-w-xs bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(6)">
                <div class="truncate" [title]="job.pickupLocation || 'N/A'">
                  {{ job.pickupLocation || 'N/A' }}
                </div>
              </td>
              <td class="px-4 py-3 text-sm text-gray-600 max-w-xs bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(7)">
                <div class="truncate" [title]="job.destinationAddress || 'N/A'">
                  {{ job.destinationAddress || 'N/A' }}
                </div>
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-600 bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(8)">{{ job.serviceType || 'N/A' }}</td>
              <td class="px-4 py-3 whitespace-nowrap bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(9)">
                @if (job.priority) {
                  <span class="px-2 py-1 text-xs font-semibold rounded-full
                    {{ job.priority === 'Emergency' ? 'bg-red-100 text-red-800' : 
                       job.priority === 'Normal' ? 'bg-blue-100 text-blue-800' : 
                       'bg-gray-100 text-gray-800' }}">
                    {{ job.priority }}
                  </span>
                } @else {
                  <span class="text-gray-400">N/A</span>
                }
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-600 bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(10)">
                {{ job.driverName || 'Unassigned' }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(11)">
                {{ formatCurrency(job.cost) }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(12)">
                <span
                  class="px-2 py-1 text-xs font-semibold rounded-full {{ getPaymentStatusBadgeClass(job.paymentStatus) }}"
                  [title]="job.paidAt ? ('Paid at ' + formatDate(job.paidAt)) : (job.paymentMethod || '')"
                >
                  {{ jobPaymentStatusLabel(job) }}
                </span>
                @if (job.paymentMethod && jobPaymentStatusLabel(job) !== 'Unpaid') {
                  <div class="text-[10px] text-gray-500 mt-0.5 max-w-[5.5rem] truncate" [title]="job.paymentMethod">
                    {{ job.paymentMethod }}
                  </div>
                }
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-600 bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(13)">
                {{ job.invoiceNumber || 'N/A' }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-600 bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(14)">
                {{ job.companyName || 'N/A' }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-600 bg-white group-hover:bg-gray-50" [ngStyle]="getStickyTdStyle(15)">
                {{ formatDate(job.createdAt) }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-right text-sm font-medium bg-white group-hover:bg-gray-50 min-w-[8rem]" [ngStyle]="getStickyTdStyle(16)">
                <div class="flex items-center justify-end gap-1.5 flex-nowrap flex-shrink-0">
                  <button
                    type="button"
                    (click)="openJobDetails(job)"
                    class="text-primary hover:text-primary-dark p-1 flex-shrink-0"
                    title="View details"
                  >
                    <i class="fas fa-eye"></i>
                  </button>
                  @if (isJobEligibleForPaymentLink(job)) {
                    <button
                      type="button"
                      (click)="openCardGatewayForJob(job)"
                      [disabled]="quickPaySubmittingJobId === job.id"
                      class="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-800 hover:bg-indigo-100 disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
                      title="Open Stripe card payment gateway"
                    >
                      @if (quickPaySubmittingJobId === job.id) {
                        <i class="fas fa-spinner fa-spin"></i>
                      } @else {
                        <i class="fas fa-credit-card"></i>
                      }
                      <span class="hidden sm:inline">Pay card</span>
                    </button>
                    <button
                      type="button"
                      (click)="openPaymentLinkModal(job)"
                      class="inline-flex items-center gap-1 rounded-md border border-teal-200 bg-teal-50 px-2 py-1 text-xs font-medium text-teal-800 hover:bg-teal-100 flex-shrink-0"
                      title="Create Stripe payment link for the client"
                    >
                      <i class="fas fa-link"></i>
                      <span class="hidden sm:inline">Pay link</span>
                    </button>
                  }
                  @if (getNextStatuses(job).length) {
                    <button
                      type="button"
                      (click)="openUpdateStatusModal(job)"
                      class="text-green-600 hover:text-green-800 p-1 flex-shrink-0"
                      title="Update status"
                    >
                      <i class="fas fa-sync-alt"></i>
                    </button>
                  }
                  @if (canAssignOrReassignDriver(job)) {
                    <button
                      type="button"
                      (click)="openAssignDriverModal(job)"
                      class="text-blue-600 hover:text-blue-800 p-1 flex-shrink-0"
                      [title]="job.status === JOB_STATUS.Waiting ? 'Assign driver' : 'Re-assign driver'"
                    >
                      <i class="fas" [class.fa-user-plus]="job.status === JOB_STATUS.Waiting" [class.fa-random]="job.status !== JOB_STATUS.Waiting"></i>
                    </button>
                  }
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }

  <!-- Empty State -->
  @if (!loading && filteredJobs.length === 0) {
    <div class="bg-white rounded-lg shadow-sm p-12 text-center">
      <i class="fas fa-tasks text-6xl text-gray-300 mb-4"></i>
      <h3 class="text-xl font-semibold text-gray-900 mb-2">No jobs found</h3>
      <p class="text-gray-600 mb-6">Get started by creating your first job</p>
      <button
        (click)="openCreateJobModal()"
        class="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium"
      >
        <i class="fas fa-plus mr-2"></i>Create New Job
      </button>
    </div>
  }

  <!-- Create Job Modal -->
  @if (showCreateJobModal) {
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] flex flex-col">
        <div class="px-6 py-5 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-2xl font-bold text-gray-800 tracking-tight">Create New Job</h2>
              <p class="text-sm text-gray-600 mt-1">Switch between tabs anytime to complete the form.</p>
            </div>
            <button
              type="button"
              (click)="closeCreateJobModal()"
              class="text-gray-600 hover:text-gray-800 transition-colors p-1 hover:bg-gray-200 rounded-full"
            >
              <i class="fas fa-times text-lg"></i>
            </button>
          </div>
          <div class="mt-4 border-b border-gray-200">
            <nav class="-mb-px flex items-center gap-6 justify-center" aria-label="Create Job Tabs">
              <button
                type="button"
                role="tab"
                [attr.aria-selected]="activeCreateJobTab === 'details'"
                (click)="setCreateJobTab('details')"
                [class.text-primary]="activeCreateJobTab === 'details'"
                [class.border-primary]="activeCreateJobTab === 'details'"
                [class.text-gray-500]="activeCreateJobTab !== 'details'"
                [class.border-transparent]="activeCreateJobTab !== 'details'"
                class="whitespace-nowrap pb-3 px-1 border-b-2 text-sm font-semibold transition-colors hover:text-gray-700"
              >
                Job Details
              </button>
              <button
                type="button"
                role="tab"
                [attr.aria-selected]="activeCreateJobTab === 'payment'"
                (click)="setCreateJobTab('payment')"
                [class.text-primary]="activeCreateJobTab === 'payment'"
                [class.border-primary]="activeCreateJobTab === 'payment'"
                [class.text-gray-500]="activeCreateJobTab !== 'payment'"
                [class.border-transparent]="activeCreateJobTab !== 'payment'"
                class="whitespace-nowrap pb-3 px-1 border-b-2 text-sm font-semibold transition-colors hover:text-gray-700"
              >
                Payment Details
              </button>
            </nav>
          </div>
        </div>

        <!-- Scrollable Content Area -->
        <div class="flex-1 overflow-y-auto">
          <form [formGroup]="createJobForm" (ngSubmit)="onCreateJob()" class="p-6">
          <!-- Validation Error Summary -->
          @if (createJobError && showCreateJobModal && validationErrorList.length > 0) {
            <div id="validation-errors" class="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <div class="flex">
                <div class="flex-shrink-0">
                  <i class="fas fa-exclamation-circle text-red-500 text-xl"></i>
                </div>
                <div class="ml-3 flex-1">
                  <h3 class="text-sm font-medium text-red-800 mb-2">{{ createJobError }}</h3>
                  <ul class="list-disc list-inside space-y-1">
                    @for (errorMsg of validationErrorList; track errorMsg) {
                      <li class="text-sm text-red-700">{{ errorMsg }}</li>
                    }
                  </ul>
                </div>
              </div>
            </div>
          }

          @if (createJobError && showCreateJobModal && validationErrorList.length === 0) {
            <div id="validation-errors" class="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <div class="flex">
                <div class="flex-shrink-0">
                  <i class="fas fa-exclamation-circle text-red-500 text-xl"></i>
                </div>
                <div class="ml-3 flex-1">
                  <h3 class="text-sm font-medium text-red-800">{{ createJobError }}</h3>
                </div>
              </div>
            </div>
          }

          <p class="mb-4 text-sm text-gray-600">
            <span class="text-red-500">*</span> Required fields
          </p>

          <!-- Step 1: Call Type & Basic Info -->
          @if (activeCreateJobTab === 'details') {
            <div class="space-y-6 animate-fadeIn">
              <div class="mb-6">
                <h3 class="text-xl font-bold text-gray-900">Call Type & Basic Info</h3>
                <p class="text-sm text-gray-600">Select the call type and provide basic information</p>
              </div>
            <!-- Call Type Section -->
            <div class="border-b border-gray-200 pb-4 ">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Call Type</h3>
              <div class="flex flex-wrap items-center justify-center gap-2 mb-4">
                @for (callType of callTypes; track callType) {
                  <label class="relative flex items-center justify-center cursor-pointer">
                    <input
                      type="radio"
                      formControlName="callType"
                      [value]="callType"
                      class="sr-only peer"
                    />
                    <span 
                      class="px-3 py-1.5 text-xs font-medium text-center rounded-lg border-2 transition-all duration-200
                             border-gray-300 bg-white text-gray-700 whitespace-nowrap
                             peer-checked:border-primary peer-checked:bg-primary peer-checked:text-white
                             hover:border-gray-400 hover:shadow-sm
                             peer-checked:hover:bg-primary peer-checked:hover:border-primary-dark"
                    >
                      {{ callType }}
                    </span>
                  </label>
                }
              </div>
              
              @if (createJobForm.get('callType')?.value === 'Schedule a call') {
                <div class="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Scheduled Date</label>
                    <input
                      type="date"
                      formControlName="scheduledDate"
                      class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Scheduled Time</label>
                    <input
                      type="time"
                      formControlName="scheduledTime"
                      class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              }
            </div>

            <!-- Company & Account Section -->
            <div class="border-b border-gray-200 pb-4 ">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Company & Account</h3>
              <div class="space-y-4 flex items-end gap-2">
                <div class="w-1/3 h-[120px]">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                  <input
                    type="text"
                    formControlName="companyName"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                    placeholder="Enter company name"
                    [disabled]="true"
                    [readonly]="true"
                  />
                </div>
                <div class="w-1/3 h-[120px]">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Account</label>
                  <select
                    formControlName="account"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary bg-white"
                    [disabled]="insuranceAccountsLoading"
                  >
                    <option value="">Select an account</option>
                    @for (acc of insuranceAccounts; track acc.id) {
                      <option [value]="acc.id">{{ insuranceAccountOptionLabel(acc) }}</option>
                    }
                  </select>
                  @if (insuranceAccountsLoading) {
                    <p class="mt-1 text-xs text-gray-500">Loading accounts…</p>
                  }
                  @if (!insuranceAccountsLoading && insuranceAccounts.length === 0) {
                    <p class="mt-1 text-xs text-amber-700">
                      No insurance accounts available. An administrator can add them under Admin → Accounts.
                    </p>
                  }
                </div>
                <div class="w-1/3 h-[120px]">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Company Override</label>
                  <select
                    formControlName="companyOverride"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select what company name to show on invoice</option>
                    <option value="Strong Towing Inc">Strong Towing Inc</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Priority -->
            <div class="pb-4 ">
              <label class="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <div class="flex gap-4">
                @for (priority of priorities; track priority) {
                  <label class="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      formControlName="priority"
                      [value]="priority"
                      class="mr-2"
                    />
                    <span class="text-sm font-medium text-gray-700">{{ priority }}</span>
                  </label>
                }
              </div>
            </div>
            </div>
          }

          <!-- Step 2: Location & Service -->
          @if (activeCreateJobTab === 'details') {
            <div class="space-y-6 animate-fadeIn">
              <div class="mb-6">
                <h3 class="text-xl font-bold text-gray-900">Location & Service</h3>
                <p class="text-sm text-gray-600">Provide location details and service information</p>
              </div>

            <!-- Location Section -->
            <div class="border-b border-gray-200 pb-4 ">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Location</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Pickup Location <span class="text-red-500">*</span>
                  </label>
                  <div class="flex gap-2 items-stretch">
                    <input
                      type="text"
                      formControlName="pickupLocation"
                      appPlacesAutocomplete
                      autocomplete="off"
                      placeholder="Start typing — address autocomplete"
                      [class.border-red-500]="showCreateJobFieldError(createJobForm.get('pickupLocation'))"
                      class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                    <button
                      type="button"
                      (click)="openLocationMapPicker('pickup')"
                      title="Pick on map"
                      class="shrink-0 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-primary"
                      aria-label="Open map to pick pickup location"
                    >
                      <i class="fas fa-map-marker-alt"></i>
                    </button>
                  </div>
                  @if (showCreateJobFieldError(createJobForm.get('pickupLocation'))) {
                    <p class="mt-1 text-sm text-red-600">
                      <i class="fas fa-exclamation-circle mr-1"></i>Pickup location is required
                    </p>
                  }
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Destination Address</label>
                  <div class="flex gap-2 items-stretch">
                    <input
                      type="text"
                      formControlName="destinationAddress"
                      appPlacesAutocomplete
                      autocomplete="off"
                      placeholder="Start typing — address autocomplete"
                      class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                    <button
                      type="button"
                      (click)="openLocationMapPicker('destination')"
                      title="Pick on map"
                      class="shrink-0 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-primary"
                      aria-label="Open map to pick destination"
                    >
                      <i class="fas fa-map-marker-alt"></i>
                    </button>
                  </div>
                </div>
              </div>
              @if (
                (createJobForm.get('pickupLocation')?.value ?? '').toString().trim() &&
                (createJobForm.get('destinationAddress')?.value ?? '').toString().trim()
              ) {
                <div class="mt-3 flex flex-wrap items-center gap-2 text-sm">
                  @if (pickupToDestinationMilesLoading) {
                    <span class="text-gray-600">
                      <i class="fas fa-spinner fa-spin mr-1"></i>Calculating route…
                    </span>
                  } @else if (pickupToDestinationMilesError) {
                    <span class="text-amber-800">
                      <i class="fas fa-exclamation-triangle mr-1"></i>{{ pickupToDestinationMilesError }}
                    </span>
                  } @else if (pickupToDestinationMiles !== null) {
                    <span class="text-gray-800">
                      <i class="fas fa-road mr-1 text-gray-500"></i>
                      Pickup → drop-off:
                      <strong class="ml-1">{{ pickupToDestinationMiles | number: '1.2-2' }} mi</strong>
                      @if (pickupToDestinationDurationText) {
                        <span class="text-gray-600 mx-1">·</span>
                        <i class="far fa-clock mr-1 text-gray-500"></i>
                        <span>~{{ pickupToDestinationDurationText }}</span>
                      }
                    </span>
                  }
                </div>
              }
            </div>

            <!-- Job Details Section -->
            <div class="">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Job Details</h3>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Service Type <span class="text-red-500">*</span>
                  </label>
                  <select 
                    formControlName="serviceType" 
                    [class.border-red-500]="showCreateJobFieldError(createJobForm.get('serviceType'))"
                    [disabled]="servicePricingProfilesLoading || serviceTypes.length === 0"
                    class="w-full px-4 h-[42px] py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                  >
                    @if (servicePricingProfilesLoading) {
                      <option value="">Loading services...</option>
                    } @else if (serviceTypes.length === 0) {
                      <option value="">No services configured</option>
                    } @else {
                      <option value="">Select service type</option>
                    }
                    @for (type of serviceTypes; track type) {
                      <option [value]="type">{{ type }}</option>
                    }
                  </select>
                  @if (showCreateJobFieldError(createJobForm.get('serviceType'))) {
                    <p class="mt-1 text-sm text-red-600">
                      <i class="fas fa-exclamation-circle mr-1"></i>Service type is required
                    </p>
                  }
                  @if (getSelectedServicePricingProfile(); as pricingProfile) {
                    <p class="mt-1 text-xs text-gray-600">
                      Service defaults applied: Loaded {{ pricingProfile.loadedPrice | number:'1.2-2' }}, Deadhead {{ pricingProfile.deadHeadPrice | number:'1.2-2' }}.
                    </p>
                  }
                </div>
              </div>
            </div>
            </div>
          }

          <!-- Step 3: Client Information -->
          @if (activeCreateJobTab === 'details') {
            <div class="space-y-6 animate-fadeIn">
              <div class="mb-6">
                <h3 class="text-xl font-bold text-gray-900">Client Information</h3>
                <p class="text-sm text-gray-600">Select or create client details</p>
              </div>

            <!-- Client Section -->
            <div class="border-b border-gray-200 pb-4 " formGroupName="client">
              <!-- Client Mode Toggle -->
              <div class="mb-4">
                <div class="flex gap-4">
                  <label class="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      [checked]="useExistingClient"
                      (change)="useExistingClient = true; onClientModeChange()"
                      class="mr-2"
                    />
                    <span class="text-sm font-medium text-gray-700">Use Existing Client</span>
                  </label>
                  <label class="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      [checked]="!useExistingClient"
                      (change)="useExistingClient = false; onClientModeChange()"
                      class="mr-2"
                    />
                    <span class="text-sm font-medium text-gray-700">Create New Client</span>
                  </label>
                </div>
              </div>

              @if (useExistingClient) {
                <!-- Existing Client Selection (searchable) -->
                <div class="relative" #createJobClientSelectRoot>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Select Client <span class="text-red-500">*</span>
                  </label>
                  <input type="hidden" formControlName="clientId" />
                  <input
                    type="text"
                    [(ngModel)]="createJobClientSearch"
                    [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="onCreateJobClientSearchChange()"
                    (focus)="openCreateJobClientSelect()"
                    autocomplete="off"
                    placeholder="Search by name, phone, email, or id…"
                    [class.border-red-500]="showCreateJobFieldError(createJobForm.get('client.clientId'))"
                    class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                  />
                  @if (createJobClientSelectOpen) {
                    <ul
                      class="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                      role="listbox"
                    >
                      @if (createJobClientSearchLoading) {
                        <li class="px-4 py-2 text-sm text-gray-500">Loading…</li>
                      } @else if (createJobClientSearchResults.length === 0) {
                        <li class="px-4 py-2 text-sm text-gray-500">No clients match your search</li>
                      } @else {
                        @for (client of createJobClientSearchResults; track client.id) {
                          <li
                            role="option"
                            class="cursor-pointer px-4 py-2 text-sm hover:bg-gray-100"
                            (mousedown)="$event.preventDefault()"
                            (click)="selectCreateJobClient(client, $event)"
                          >
                            {{ formatCreateJobClientLabel(client) }}
                          </li>
                        }
                      }
                    </ul>
                  }
                  @if (showCreateJobFieldError(createJobForm.get('client.clientId'))) {
                    <p class="mt-1 text-sm text-red-600">
                      <i class="fas fa-exclamation-circle mr-1"></i>Client selection is required
                    </p>
                  }
                </div>
              } @else {
                <!-- New Client Form -->
                <div class="space-y-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span class="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      formControlName="clientPhoneNumber"
                      placeholder="+1234567890"
                      [class.border-red-500]="showCreateJobFieldError(createJobForm.get('client.clientPhoneNumber'))"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    />
                    @if (showCreateJobFieldError(createJobForm.get('client.clientPhoneNumber'))) {
                      <p class="mt-1 text-sm text-red-600">
                        <i class="fas fa-exclamation-circle mr-1"></i>
                        @if (createJobForm.get('client.clientPhoneNumber')?.hasError('required')) {
                          Phone number is required
                        } @else {
                          Please enter a valid phone number
                        }
                      </p>
                    }
                    <p class="mt-1 text-xs text-gray-500">
                      <i class="fas fa-info-circle mr-1"></i>
                      Phone number is used as the unique identifier for this client
                    </p>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Full Name <span class="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      formControlName="clientFullName"
                      placeholder="John Doe"
                      [class.border-red-500]="showCreateJobFieldError(createJobForm.get('client.clientFullName'))"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    />
                    @if (showCreateJobFieldError(createJobForm.get('client.clientFullName'))) {
                      <p class="mt-1 text-sm text-red-600">
                        <i class="fas fa-exclamation-circle mr-1"></i>
                        @if (createJobForm.get('client.clientFullName')?.hasError('required')) {
                          Full name is required
                        } @else if (createJobForm.get('client.clientFullName')?.hasError('minlength')) {
                          Full name must be at least 2 characters
                        }
                      </p>
                    }
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      formControlName="clientEmail"
                      placeholder="client@example.com (optional)"
                      [class.border-red-500]="showCreateJobFieldError(createJobForm.get('client.clientEmail'))"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    />
                    @if (showCreateJobFieldError(createJobForm.get('client.clientEmail'))) {
                      <p class="mt-1 text-sm text-red-600">
                        <i class="fas fa-exclamation-circle mr-1"></i>
                        Please enter a valid email address
                      </p>
                    }
                  </div>
                </div>
              }
            </div>
            </div>
          }

          <!-- Step 4: Vehicle Information -->
          @if (activeCreateJobTab === 'details') {
            <div class="space-y-6 animate-fadeIn ">
              <div class="mb-6">
                <h3 class="text-xl font-bold text-gray-900">Vehicle Information</h3>
                <p class="text-sm text-gray-600">Select or create vehicle details</p>
              </div>

            <!-- Vehicle Section -->
            <div class="border-b border-gray-200 pb-4 " formGroupName="vehicle">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Vehicle Information</h3>
              
              <!-- Vehicle Mode Toggle -->
              <div class="mb-4">
                <div class="flex gap-4">
                  <label class="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      [checked]="useExistingVehicle"
                      (change)="useExistingVehicle = true; onVehicleModeChange()"
                      class="mr-2"
                    />
                    <span class="text-sm font-medium text-gray-700">Use Existing Vehicle</span>
                  </label>
                  <label class="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      [checked]="!useExistingVehicle"
                      (change)="useExistingVehicle = false; onVehicleModeChange()"
                      class="mr-2"
                    />
                    <span class="text-sm font-medium text-gray-700">Create New Vehicle</span>
                  </label>
                </div>
              </div>

              @if (useExistingVehicle) {
                <!-- Existing Vehicle Selection -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Select Vehicle <span class="text-red-500">*</span>
                  </label>
                  <select 
                    formControlName="vehicleId" 
                    [class.border-red-500]="showCreateJobFieldError(createJobForm.get('vehicle.vehicleId'))"
                    [disabled]="useExistingClient && !createJobForm.get('client.clientId')?.value"
                    class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                  >
                    @if (useExistingClient && !createJobForm.get('client.clientId')?.value) {
                      <option value="">Select a client first</option>
                    } @else {
                      <option value="">Select a vehicle</option>
                    }
                    @for (vehicle of filteredVehicles; track vehicle.id) {
                      <option [value]="vehicle.id">
                        {{ vehicle.year }} {{ vehicle.make }} {{ vehicle.model }} - {{ vehicle.vin }}
                      </option>
                    }
                  </select>
                  @if (useExistingClient && !createJobForm.get('client.clientId')?.value) {
                    <p class="mt-1 text-sm text-gray-500">
                      Select a client first to load that client's vehicles.
                    </p>
                  }
                  @if (showCreateJobFieldError(createJobForm.get('vehicle.vehicleId'))) {
                    <p class="mt-1 text-sm text-red-600">
                      <i class="fas fa-exclamation-circle mr-1"></i>Vehicle selection is required
                    </p>
                  }
                </div>
              } @else {
                <!-- New Vehicle Form -->
                <div class="space-y-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      VIN <span class="text-gray-500 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      formControlName="vehicleVin"
                      placeholder="1HGBH41JXMN109186"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div class="grid grid-cols-2 gap-4">
                    <div class="relative">
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        Make <span class="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        [value]="catalogMakeSearchDraft"
                        (input)="onMakeSearchInput($any($event.target).value)"
                        (focus)="onMakeSearchFocus()"
                        (blur)="onMakeSearchBlur()"
                        placeholder="Search make…"
                        [class.border-red-500]="showCreateJobFieldError(createJobForm.get('vehicle.vehicleMake'))"
                        class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary bg-white"
                        autocomplete="off"
                      />
                      @if (showMakeDropdown && catalogMakeSuggestions.length > 0) {
                        <ul
                          class="absolute z-30 mt-1 w-full max-h-52 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg text-sm"
                        >
                          @for (m of catalogMakeSuggestions; track m.id) {
                            <li>
                              <button
                                type="button"
                                class="w-full text-left px-3 py-2 hover:bg-gray-50"
                                (mousedown)="$event.preventDefault(); selectCatalogMake(m)"
                              >
                                {{ m.name }}
                              </button>
                            </li>
                          }
                        </ul>
                      }
                      @if (catalogMakeSearchLoading) {
                        <p class="mt-1 text-xs text-gray-500">Searching…</p>
                      }
                      @if (catalogEmpty) {
                        <p class="mt-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                          No catalog data. Ask an admin to run <strong>Settings → Vehicle catalog → Sync from NHTSA</strong>.
                        </p>
                      }
                      <p class="mt-1 text-xs text-gray-500">US-market data (NHTSA), stored locally.</p>
                      @if (showCreateJobFieldError(createJobForm.get('vehicle.vehicleMake'))) {
                        <p class="mt-1 text-sm text-red-600">
                          <i class="fas fa-exclamation-circle mr-1"></i>Make is required
                        </p>
                      }
                    </div>
                    <div class="relative">
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        Model <span class="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        [value]="catalogModelSearchDraft"
                        (input)="onModelSearchInput($any($event.target).value)"
                        (focus)="onModelSearchFocus()"
                        (blur)="onModelSearchBlur()"
                        placeholder="Search model…"
                        [disabled]="!createJobForm.get('vehicle.vehicleCatalogMakeId')?.value"
                        [class.border-red-500]="showCreateJobFieldError(createJobForm.get('vehicle.vehicleModel'))"
                        [class.opacity-60]="!createJobForm.get('vehicle.vehicleCatalogMakeId')?.value"
                        class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary bg-white"
                        autocomplete="off"
                      />
                      @if (
                        showModelDropdown &&
                        createJobForm.get('vehicle.vehicleCatalogMakeId')?.value &&
                        catalogModelSuggestions.length > 0
                      ) {
                        <ul
                          class="absolute z-30 mt-1 w-full max-h-52 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg text-sm"
                        >
                          @for (md of catalogModelSuggestions; track md.id) {
                            <li>
                              <button
                                type="button"
                                class="w-full text-left px-3 py-2 hover:bg-gray-50"
                                (mousedown)="$event.preventDefault(); selectCatalogModel(md)"
                              >
                                {{ md.name }}
                              </button>
                            </li>
                          }
                        </ul>
                      }
                      @if (catalogModelSearchLoading) {
                        <p class="mt-1 text-xs text-gray-500">Searching…</p>
                      }
                      @if (catalogModelEmpty && createJobForm.get('vehicle.vehicleCatalogMakeId')?.value) {
                        <p class="mt-1 text-xs text-gray-500">No matching models. Try another search.</p>
                      }
                      @if (showCreateJobFieldError(createJobForm.get('vehicle.vehicleModel'))) {
                        <p class="mt-1 text-sm text-red-600">
                          <i class="fas fa-exclamation-circle mr-1"></i>Model is required
                        </p>
                      }
                    </div>
                  </div>
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        Year <span class="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        formControlName="vehicleYear"
                        placeholder="2021"
                        min="1900"
                        max="2100"
                        [class.border-red-500]="showCreateJobFieldError(createJobForm.get('vehicle.vehicleYear'))"
                        class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      />
                      @if (showCreateJobFieldError(createJobForm.get('vehicle.vehicleYear'))) {
                        <p class="mt-1 text-sm text-red-600">
                          <i class="fas fa-exclamation-circle mr-1"></i>
                          @if (createJobForm.get('vehicle.vehicleYear')?.hasError('required')) {
                            Year is required
                          } @else {
                            Year must be between 1900 and 2100
                          }
                        </p>
                      }
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">Color</label>
                      <input
                        type="text"
                        formControlName="vehicleColor"
                        placeholder="Blue"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                  
                  <!-- Additional Vehicle Fields -->
                  <div class="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">License Plate</label>
                      <input
                        type="text"
                        formControlName="licensePlate"
                        placeholder="ABC123"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">State</label>
                      <input
                        type="text"
                        formControlName="licenseState"
                        placeholder="CA"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                  
                  <div class="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">Drive Type</label>
                      <select
                        formControlName="driveType"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Select drive type</option>
                        @for (driveType of driveTypes; track driveType) {
                          <option [value]="driveType">{{ driveType }}</option>
                        }
                      </select>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">Vehicle Type</label>
                      <select
                        formControlName="vehicleType"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Select vehicle type</option>
                        @for (vehicleType of vehicleTypes; track vehicleType) {
                          <option [value]="vehicleType">{{ vehicleType }}</option>
                        }
                      </select>
                    </div>
                  </div>
                  
                  <div class="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">Odometer</label>
                      <input
                        type="number"
                        formControlName="odometer"
                        placeholder="0"
                        min="0"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">Drivable</label>
                      <select
                        formControlName="drivable"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Select</option>
                        @for (option of drivableOptions; track option) {
                          <option [value]="option">{{ option }}</option>
                        }
                      </select>
                    </div>
                  </div>
                  
                  <div class="mt-4">
                    <label class="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        formControlName="haveKeys"
                        class="mr-2"
                      />
                      <span class="text-sm font-medium text-gray-700">Have Keys</span>
                    </label>
                    @if (createJobForm.get('vehicle.haveKeys')?.value) {
                      <div class="mt-2">
                        <label class="block text-sm font-medium text-gray-700 mb-2">Key Location</label>
                        <input
                          type="text"
                          formControlName="keyLocation"
                          placeholder="Where are the keys located?"
                          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
            </div>
          }

          <!-- Step 5: Assignment & Notes -->
          @if (activeCreateJobTab === 'details') {
            <div class="space-y-6 animate-fadeIn ">
              <div class="mb-6">
                <h3 class="text-xl font-bold text-gray-900">Assignment & Notes</h3>
                <p class="text-sm text-gray-600">Assign driver and add notes</p>
              </div>

            <!-- Assignment Section -->
            <div class="border-b border-gray-200 pb-4 ">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Assignment</h3>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Driver</label>
                  <select
                    formControlName="driverId"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select a driver</option>
                    @for (driver of availableDrivers; track driver.id) {
                      <option [value]="driver.id">{{ driver.fullName }} - {{ driver.email }}</option>
                    }
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Truck</label>
                  <select
                    formControlName="truckId"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select a truck</option>
                    @for (truck of trucks; track truck.id) {
                      <option [value]="truck.id">{{ truck.name || truck.id }}</option>
                    }
                  </select>
                </div>
              </div>
            </div>

            <!-- Notes Section -->
            <div class="border-b border-gray-200 pb-4">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  formControlName="notes"
                  rows="3"
                  placeholder="Additional information about the job..."
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                ></textarea>
              </div>
            </div>

            <!-- Billing Notes Section -->
            <div class="pb-4 ">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Billing Notes</h3>
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Billing Notes</label>
                  <textarea
                    formControlName="billingNotes"
                    rows="3"
                    placeholder="Notes for billing department..."
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  ></textarea>
                </div>
                <div>
                  <label class="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      formControlName="includeBillingNotesOnReceipt"
                      class="mr-2"
                    />
                    <span class="text-sm font-medium text-gray-700">Include billing notes on the receipt</span>
                  </label>
                </div>
              </div>
            </div>
            </div>
          }

          <!-- Step 6: Invoice & Charges -->
          @if (activeCreateJobTab === 'payment') {
            <div class="space-y-6 animate-fadeIn">
              <div class="mb-6">
                <h3 class="text-xl font-bold text-gray-900">Invoice & Charges</h3>
                <p class="text-sm text-gray-600">Set up pricing and charges</p>
              </div>

            <!-- Invoice Charges Section -->
            <div class="border-b border-gray-200 pb-4" formGroupName="invoiceCharges">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Invoice Charges</h3>

              <div class="mb-4 rounded-lg border border-gray-200 bg-slate-50 px-4 py-3 text-sm text-gray-700">
                <p class="font-medium text-gray-900 mb-1">Dispatch office (point A)</p>
                @if (dispatchOfficeLoadError) {
                  <p class="text-amber-800">{{ dispatchOfficeLoadError }}</p>
                } @else if (dispatchOfficeCoords) {
                  <p>
                    <span class="font-mono text-gray-800">{{ dispatchOfficeCoords.lat | number:'1.4-4' }}, {{ dispatchOfficeCoords.lng | number:'1.4-4' }}</span>
                    <span class="text-gray-600"> — Used for office → pickup and drop-off → office when you calculate price.</span>
                  </p>
                }
                <p class="text-xs text-gray-500 mt-1">Set in Admin → Settings → Dispatch office location.</p>
              </div>
              
              <!-- Office → pickup (unloaded / enroute) -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">Office → pickup (unloaded / enroute)</label>
                <p class="text-xs text-gray-500 mb-2">Truck not hooked; from dispatch office to the pickup location.</p>
                <div class="grid grid-cols-3 gap-4">
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Miles</label>
                    <input
                      type="number"
                      formControlName="unloadedEnrouteMileageQuantity"
                      (input)="calculateInvoiceTotals()"
                      min="0"
                      class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Price</label>
                    <input
                      type="number"
                      formControlName="unloadedEnrouteMileagePrice"
                      (input)="calculateInvoiceTotals()"
                      step="0.01"
                      min="0"
                      class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Total</label>
                    <input
                      type="text"
                      [value]="((createJobForm.get('invoiceCharges.unloadedEnrouteMileageQuantity')?.value || 0) * (createJobForm.get('invoiceCharges.unloadedEnrouteMileagePrice')?.value || 0)).toFixed(2)"
                      readonly
                      class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                </div>
              </div>

              <!-- Pickup → drop-off (loaded / hooked) -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">Pickup → drop-off (loaded / hooked)</label>
                <p class="text-xs text-gray-500 mb-2">Vehicle hooked; from pickup address to drop-off address.</p>
                <div class="grid grid-cols-3 gap-4">
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Miles</label>
                    <input
                      type="number"
                      formControlName="loadedHookedMileageQuantity"
                      (input)="calculateInvoiceTotals()"
                      min="0"
                      class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Price</label>
                    <input
                      type="number"
                      formControlName="loadedHookedMileagePrice"
                      (input)="calculateInvoiceTotals()"
                      step="0.01"
                      min="0"
                      class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Total</label>
                    <input
                      type="text"
                      [value]="((createJobForm.get('invoiceCharges.loadedHookedMileageQuantity')?.value || 0) * (createJobForm.get('invoiceCharges.loadedHookedMileagePrice')?.value || 0)).toFixed(2)"
                      readonly
                      class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                </div>
              </div>

              <!-- Drop-off → office (deadhead) -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">Drop-off → office (deadhead)</label>
                <p class="text-xs text-gray-500 mb-2">Empty return from drop-off back to the dispatch office.</p>
                <div class="grid grid-cols-3 gap-4">
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Miles</label>
                    <input
                      type="number"
                      formControlName="deadHeadMileageQuantity"
                      (input)="calculateInvoiceTotals()"
                      min="0"
                      class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Price</label>
                    <input
                      type="number"
                      formControlName="deadHeadMileagePrice"
                      (input)="calculateInvoiceTotals()"
                      step="0.01"
                      min="0"
                      class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Total</label>
                    <input
                      type="text"
                      [value]="((createJobForm.get('invoiceCharges.deadHeadMileageQuantity')?.value || 0) * (createJobForm.get('invoiceCharges.deadHeadMileagePrice')?.value || 0)).toFixed(2)"
                      readonly
                      class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                </div>
              </div>

              <!-- Hookup fee -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Hookup Fee</label>
                <input
                  type="number"
                  formControlName="hookupFee"
                  (input)="calculateInvoiceTotals()"
                  step="0.01"
                  min="0"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>

              <!-- Service Items -->
              <div class="mb-4">
                <div class="flex items-center justify-between mb-2">
                  <label class="block text-sm font-medium text-gray-700">Service Items</label>
                  <button
                    type="button"
                    (click)="addServiceItem()"
                    class="text-sm text-primary hover:text-primary-dark"
                  >
                    <i class="fas fa-plus mr-1"></i>Add Service Item
                  </button>
                </div>
                @for (item of invoiceServiceItems; track $index) {
                  <div class="grid grid-cols-4 gap-4 mb-2">
                    <div>
                      <input
                        type="text"
                        [value]="item.serviceName"
                        (input)="item.serviceName = $any($event.target).value; updateServiceItemTotal($index)"
                        placeholder="Service name"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        [value]="item.quantity"
                        (input)="item.quantity = $any($event.target).value ? parseFloat($any($event.target).value) : 0; updateServiceItemTotal($index)"
                        min="0"
                        placeholder="Qty"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        [value]="item.price"
                        (input)="item.price = $any($event.target).value ? parseFloat($any($event.target).value) : 0; updateServiceItemTotal($index)"
                        step="0.01"
                        min="0"
                        placeholder="Price"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div class="flex items-center gap-2">
                      <input
                        type="text"
                        [value]="(item.quantity * item.price).toFixed(2)"
                        readonly
                        class="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      />
                      <button
                        type="button"
                        (click)="removeServiceItem($index)"
                        class="text-red-600 hover:text-red-800"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                }
              </div>

              <!-- Discount and Tax Exempt -->
              <div class="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Discount ($ amount)</label>
                  <input
                    type="number"
                    formControlName="discount"
                    (input)="calculateInvoiceTotals()"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Discount (%)</label>
                  <input
                    type="number"
                    formControlName="discountPercent"
                    (input)="calculateInvoiceTotals()"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="0.00"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div class="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Service Charge %</label>
                  <input
                    type="number"
                    formControlName="serviceChargePercent"
                    (input)="calculateInvoiceTotals()"
                    step="0.01"
                    min="0"
                    max="100"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Tax %</label>
                  <input
                    type="number"
                    formControlName="taxPercent"
                    (input)="calculateInvoiceTotals()"
                    step="0.01"
                    min="0"
                    max="100"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div class="flex items-center">
                  <label class="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      formControlName="taxExempt"
                      (change)="calculateInvoiceTotals()"
                      class="mr-2"
                    />
                    <span class="text-sm font-medium text-gray-700">Tax Exempt</span>
                  </label>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Manual Total Override</label>
                  <input
                    type="number"
                    formControlName="manualTotalOverride"
                    step="0.01"
                    min="0"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Override Reason</label>
                  <input
                    type="text"
                    formControlName="manualOverrideReason"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    placeholder="Required if policy enforces reason"
                  />
                </div>
              </div>

              <div class="mb-4">
                <button
                  type="button"
                  (click)="calculatePrice()"
                  [disabled]="calculatingPrice"
                  class="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                >
                  @if (calculatingPrice) {
                    <i class="fas fa-spinner fa-spin mr-2"></i>Calculating...
                  } @else {
                    <i class="fas fa-calculator mr-2"></i>Calculate Price
                  }
                </button>
                @if (officeToPickupMiles !== null || pickupToDestinationMiles !== null || dropoffToOfficeMiles !== null) {
                  <p class="mt-2 text-xs text-gray-600 leading-relaxed">
                    Office → pickup: {{ officeToPickupMiles ?? 0 | number:'1.2-2' }} mi ·
                    Pickup → drop-off: {{ pickupToDestinationMiles ?? 0 | number:'1.2-2' }} mi ·
                    Drop-off → office: {{ dropoffToOfficeMiles ?? 0 | number:'1.2-2' }} mi
                  </p>
                }
              </div>

              <!-- Summary -->
              <div class="border-t border-gray-200 pt-4 mb-6">
                <div class="space-y-2 text-right">
                  <div class="flex justify-between">
                    <span class="text-sm font-medium text-gray-700">Subtotal:</span>
                    <span class="text-sm text-gray-900">
                      {{ getSubtotal() | number:'1.2-2' }}
                    </span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm font-medium text-gray-700">Taxes:</span>
                    <span class="text-sm text-gray-900">
                      {{ getTaxes() | number:'1.2-2' }}
                    </span>
                  </div>
                  <div class="flex justify-between border-t border-gray-200 pt-2">
                    <span class="text-lg font-bold text-gray-900">Grand Total:</span>
                    <span class="text-lg font-bold text-gray-900">
                      {{ getGrandTotal() | number:'1.2-2' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Cost Section -->
            <div class="border-t border-gray-200 pt-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Final Cost</h3>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Cost <span class="text-red-500">*</span>
                </label>
                <div class="relative">
                  <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    formControlName="cost"
                    step="0.01"
                    min="0.01"
                    max="999999.99"
                    placeholder="0.00"
                    [class.border-red-500]="showCreateJobFieldError(createJobForm.get('cost'))"
                    class="w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                  />
                </div>
                @if (showCreateJobFieldError(createJobForm.get('cost'))) {
                  <p class="mt-1 text-sm text-red-600">
                    <i class="fas fa-exclamation-circle mr-1"></i>
                    @if (createJobForm.get('cost')?.hasError('required')) {
                      Cost is required
                    } @else if (createJobForm.get('cost')?.hasError('min')) {
                      Cost must be at least $0.01
                    } @else if (createJobForm.get('cost')?.hasError('max')) {
                      Cost must be less than $999,999.99
                    } @else {
                      Please enter a valid cost
                    }
                  </p>
                }
                <p class="mt-2 text-sm text-gray-600">
                  <i class="fas fa-info-circle mr-1"></i>
                  This will be automatically calculated from invoice charges, or you can enter a custom amount.
                </p>
              </div>
            </div>
            </div>
          }
          </form>
        </div>

        <div class="flex-shrink-0 border-t border-gray-200 bg-white">
          @if (createJobValidationAttempted && getValidationErrors().length > 0) {
            <div id="create-job-validation-footer" class="px-6 py-3 bg-red-50 border-b border-red-100">
              <p class="text-sm font-semibold text-red-800 mb-2">Fix the following to create the job:</p>
              <ul class="list-disc list-inside text-sm text-red-700 space-y-0.5">
                @for (err of getValidationErrors(); track err) {
                  <li>{{ err }}</li>
                }
              </ul>
            </div>
          }
          <div class="px-6 py-4 flex items-center justify-between">
            <button
              type="button"
              (click)="closeCreateJobModal()"
              class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <i class="fas fa-times mr-2"></i>Cancel
            </button>
            
            <button
              type="button"
              (click)="onCreateJob()"
              [disabled]="submitting"
              class="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              @if (submitting) {
                <i class="fas fa-spinner fa-spin mr-2"></i>
              } @else {
                <i class="fas fa-check mr-2"></i>
              }
              Create Job
            </button>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Map picker for pickup / destination (stacked above create job modal) -->
  @if (showLocationMapModal) {
    <div
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      (click)="closeLocationMapModal()"
    >
      <div
        class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        (click)="$event.stopPropagation()"
      >
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0 gap-4">
          <div class="min-w-0">
            <h2 class="text-xl font-bold text-gray-900">
              @if (locationMapTarget === 'pickup') {
                Pickup location
              } @else {
                Destination
              }
            </h2>
            <p class="text-sm text-gray-600 font-normal mt-1">
              Map opens centered on the <span class="font-medium text-gray-800">dispatch office</span> (Admin → Settings). Move the pin to set this address.
            </p>
          </div>
          <button
            type="button"
            (click)="closeLocationMapModal()"
            class="text-gray-600 hover:text-gray-800 transition-colors p-1 hover:bg-gray-200 rounded-full"
            aria-label="Close map"
          >
            <i class="fas fa-times text-lg"></i>
          </button>
        </div>
        <div class="p-4 overflow-y-auto flex-1 min-h-0">
          <app-location-picker
            mode="pick"
            [latitude]="mapPickerLat"
            [longitude]="mapPickerLng"
            pickHint="Search or tap the map to set the address."
            [markerTitle]="locationMapTarget === 'pickup' ? 'Pickup' : 'Destination'"
            pickMapHeight="320px"
            (positionChange)="onLocationMapPositionChange($event)"
          ></app-location-picker>
        </div>
      </div>
    </div>
  }

  <!-- Assign Driver Modal -->
  @if (showAssignDriverModal && selectedJob) {
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-2xl font-bold text-gray-900">{{ assignDriverModalIsReassign ? 'Re-assign driver' : 'Assign driver' }}</h2>
          <p class="text-sm text-gray-600 mt-1">Job #{{ selectedJob.id }}</p>
        </div>
        <form [formGroup]="assignDriverForm" (ngSubmit)="onAssignDriver()" class="p-6">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Select Driver *</label>
            <select formControlName="driverId" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary">
              <option value="">Select a driver</option>
              @for (driver of availableDrivers; track driver.id) {
                <option [value]="driver.id">{{ driver.fullName }} - {{ driver.email }}</option>
              }
            </select>
            @if (assignDriverForm.get('driverId')?.invalid && assignDriverForm.get('driverId')?.touched) {
              <p class="mt-1 text-sm text-red-600">Driver selection is required</p>
            }
          </div>

          <div class="flex items-center justify-end gap-3">
            <button
              type="button"
              (click)="closeAssignDriverModal()"
              class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              [disabled]="submitting || assignDriverForm.invalid"
              class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              @if (submitting) {
                <i class="fas fa-spinner fa-spin mr-2"></i>
              }
              {{ assignDriverModalIsReassign ? 'Re-assign driver' : 'Assign driver' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  }

  <!-- Payment link for client (Stripe) -->
  @if (showPaymentLinkModal && paymentLinkJob) {
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-2xl font-bold text-gray-900">Payment link</h2>
          <p class="text-sm text-gray-600 mt-1">
            Job #{{ paymentLinkJob.id }} · {{ formatCurrency(paymentLinkJob.cost) }}
          </p>
          <p class="text-sm text-gray-500 mt-2">
            Creates a secure Stripe checkout link you can send to the client to pay online.
          </p>
        </div>
        <div class="p-6 space-y-4">
          @if (paymentLinkError) {
            <div class="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
              <i class="fas fa-exclamation-circle mr-1"></i>{{ paymentLinkError }}
            </div>
          }
          @if (paymentLinkUrl) {
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Share this link</label>
              <div class="flex gap-2">
                <input
                  type="text"
                  readonly
                  [value]="paymentLinkUrl"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-800"
                />
                <button
                  type="button"
                  (click)="copyPaymentLinkToClipboard()"
                  class="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm hover:bg-gray-200"
                >
                  @if (paymentLinkCopied) {
                    <i class="fas fa-check text-green-600"></i>
                  } @else {
                    <i class="fas fa-copy"></i>
                  }
                </button>
              </div>
              @if (paymentLinkCopied) {
                <p class="text-xs text-green-600 mt-1">Copied to clipboard</p>
              }
            </div>
          }
          <div class="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              (click)="closePaymentLinkModal()"
              class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {{ paymentLinkUrl ? 'Close' : 'Cancel' }}
            </button>
            @if (!paymentLinkUrl) {
              <button
                type="button"
                (click)="createPaymentLinkForJob()"
                [disabled]="paymentLinkSubmitting"
                class="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                @if (paymentLinkSubmitting) {
                  <i class="fas fa-spinner fa-spin mr-2"></i>
                } @else {
                  <i class="fas fa-link mr-2"></i>
                }
                Generate link
              </button>
            }
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Update Status Modal -->
  @if (showUpdateStatusModal && selectedJob) {
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-2xl font-bold text-gray-900">Update Job Status</h2>
          <p class="text-sm text-gray-600 mt-1">Job #{{ selectedJob.id }}</p>
        </div>
        <div class="p-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">Select new status *</label>
          <select
            [formControl]="updateStatusControl"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
          >
            @for (status of getNextStatuses(selectedJob); track status) {
              <option [value]="status">{{ status }}</option>
            }
          </select>
          @if (updateStatusControl.invalid && updateStatusControl.touched) {
            <p class="mt-1 text-sm text-red-600">Status selection is required</p>
          }

          <div class="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              (click)="closeUpdateStatusModal()"
              class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              (click)="onUpdateStatus()"
              [disabled]="submitting || updateStatusControl.invalid"
              class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              @if (submitting) {
                <i class="fas fa-spinner fa-spin mr-2"></i>
              }
              Update Status
            </button>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Job Details Modal -->
  @if (showJobDetailsModal && selectedJob) {
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        <div class="p-6 border-b border-gray-200 flex-shrink-0">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-2xl font-bold text-gray-900">Job Details</h2>
              <p class="text-sm text-gray-600 mt-1">Job #{{ selectedJob.id }}</p>
            </div>
            <button
              (click)="closeJobDetails()"
              class="text-gray-400 hover:text-gray-600"
            >
              <i class="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>
        <div class="flex-1 overflow-y-auto p-6">
          <div class="space-y-6">
            <!-- Basic Information -->
            <div>
              <h3 class="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">Basic Information</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="md:col-span-2">
                  <h4 class="text-sm font-medium text-gray-500 mb-1">Status</h4>
                  <span class="inline-block px-3 py-1 text-sm font-semibold rounded-full {{ getStatusBadgeClass(selectedJob.status) }}">
                    {{ formatJobStatus(selectedJob.status) }}
                  </span>
                  <p class="text-xs text-gray-500 mt-2 mb-2">Full lifecycle — current step is highlighted; completed steps are green.</p>
                  <div class="flex flex-wrap gap-1.5">
                    @for (step of JOB_STATUS_ORDER_ALL; track step) {
                      <span [class]="jobStatusStepClass(selectedJob, step)">
                        {{ formatJobStatus(step) }}
                      </span>
                    }
                  </div>
                </div>
                <div>
                  <h4 class="text-sm font-medium text-gray-500 mb-1">Call Type</h4>
                  <p class="text-gray-900">{{ selectedJob.callType || 'N/A' }}</p>
                </div>
                <div>
                  <h4 class="text-sm font-medium text-gray-500 mb-1">Service Type</h4>
                  <p class="text-gray-900">{{ selectedJob.serviceType || 'N/A' }}</p>
                </div>
                <div>
                  <h4 class="text-sm font-medium text-gray-500 mb-1">Priority</h4>
                  @if (selectedJob.priority) {
                    <span class="px-2 py-1 text-xs font-semibold rounded-full
                      {{ selectedJob.priority === 'Emergency' ? 'bg-red-100 text-red-800' : 
                         selectedJob.priority === 'Normal' ? 'bg-blue-100 text-blue-800' : 
                         'bg-gray-100 text-gray-800' }}">
                      {{ selectedJob.priority }}
                    </span>
                  } @else {
                    <p class="text-gray-900">N/A</p>
                  }
                </div>
                <div>
                  <h4 class="text-sm font-medium text-gray-500 mb-1">Cost</h4>
                  <p class="text-lg font-semibold text-gray-900">{{ formatCurrency(selectedJob.cost) }}</p>
                </div>
                <div>
                  <h4 class="text-sm font-medium text-gray-500 mb-1">Payment</h4>
                  <span class="inline-block px-3 py-1 text-sm font-semibold rounded-full {{ getPaymentStatusBadgeClass(selectedJob.paymentStatus) }}">
                    {{ jobPaymentStatusLabel(selectedJob) }}
                  </span>
                  @if (selectedJob.paymentMethod) {
                    <p class="text-sm text-gray-600 mt-1">Method: {{ formatPaymentMethodLabel(selectedJob.paymentMethod) }}</p>
                  }
                  @if (selectedJob.paidAt) {
                    <p class="text-xs text-gray-500 mt-1">Paid at {{ formatDate(selectedJob.paidAt) }}</p>
                  }
                </div>
                <div>
                  <h4 class="text-sm font-medium text-gray-500 mb-1">Invoice Number</h4>
                  <p class="text-gray-900">{{ selectedJob.invoiceNumber || 'N/A' }}</p>
                </div>
                @if (selectedJob.reason) {
                  <div class="md:col-span-2">
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Reason</h4>
                    <p class="text-gray-900">{{ selectedJob.reason }}</p>
                  </div>
                }
              </div>
              @if (canOverrideJobPrice()) {
                <div class="mt-4 rounded-lg border border-amber-200 bg-amber-50/90 p-4">
                  <h4 class="text-sm font-semibold text-amber-950 mb-1">Override price (admin only)</h4>
                  <p class="text-xs text-amber-900/80 mb-3">
                    Updates the stored job total after creation. A line is appended to job notes for the audit trail.
                  </p>
                  <div class="flex flex-col gap-3 lg:flex-row lg:items-end">
                    <div class="w-full lg:w-40">
                      <label class="block text-xs font-medium text-gray-700 mb-1" for="priceOverrideCost">New total ($)</label>
                      <input
                        id="priceOverrideCost"
                        type="number"
                        step="0.01"
                        min="0.01"
                        max="999999.99"
                        [name]="'priceOverrideCost' + selectedJob.id"
                        [(ngModel)]="priceOverrideCost"
                        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                      />
                    </div>
                    <div class="min-w-0 flex-1">
                      <label class="block text-xs font-medium text-gray-700 mb-1" for="priceOverrideReason">Reason (required)</label>
                      <input
                        id="priceOverrideReason"
                        type="text"
                        [name]="'priceOverrideReason' + selectedJob.id"
                        [(ngModel)]="priceOverrideReason"
                        placeholder="Why is the price changing?"
                        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                      />
                    </div>
                    <button
                      type="button"
                      (click)="submitPriceOverride()"
                      [disabled]="priceOverrideSubmitting"
                      class="inline-flex justify-center rounded-md bg-amber-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      @if (priceOverrideSubmitting) {
                        <span>Saving…</span>
                      } @else {
                        <span>Save price</span>
                      }
                    </button>
                  </div>
                  @if (priceOverrideError) {
                    <p class="mt-2 text-sm text-red-600">{{ priceOverrideError }}</p>
                  }
                </div>
              }
              @if (canEditBilling()) {
                <div class="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/60 p-4">
                  <h4 class="text-sm font-semibold text-gray-900 mb-1">Billing &amp; payment arrangement</h4>
                  <p class="text-xs text-gray-600 mb-3">
                    Choose how this job is covered: full insurance billing, split between insurance and client, or cash collected by the driver (withheld from payroll).
                  </p>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div class="md:col-span-2">
                      <label class="block text-xs font-medium text-gray-700 mb-1">Payment arrangement</label>
                      <select
                        [(ngModel)]="billingBillingPaymentMode"
                        [name]="'billingMode' + selectedJob.id"
                        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900"
                      >
                        <option [value]="JOB_BILLING_PAYMENT_MODE.Standard">Standard (card / link / cash)</option>
                        <option [value]="JOB_BILLING_PAYMENT_MODE.InsuranceFull">Paid by insurance (full)</option>
                        <option [value]="JOB_BILLING_PAYMENT_MODE.SplitInsuranceClient">Split: insurance + client</option>
                        <option [value]="JOB_BILLING_PAYMENT_MODE.CashToDriverPayroll">Cash to driver (payroll deduction)</option>
                      </select>
                    </div>
                    @if (billingBillingPaymentMode === JOB_BILLING_PAYMENT_MODE.InsuranceFull) {
                      <div>
                        <label class="block text-xs font-medium text-gray-700 mb-1">Insurance amount ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          [(ngModel)]="billingInsuranceCoveredAmount"
                          [name]="'insFull' + selectedJob.id"
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          placeholder="Usually job total"
                        />
                      </div>
                      <div class="flex items-end">
                        <label class="inline-flex items-center gap-2 text-sm text-gray-800">
                          <input type="checkbox" [(ngModel)]="billingInsurancePortionBilled" [name]="'insBilled' + selectedJob.id" />
                          Insurance billed / confirmed
                        </label>
                      </div>
                    }
                    @if (billingBillingPaymentMode === JOB_BILLING_PAYMENT_MODE.SplitInsuranceClient) {
                      <div>
                        <label class="block text-xs font-medium text-gray-700 mb-1">Insurance covers ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          [(ngModel)]="billingInsuranceCoveredAmount"
                          [name]="'insSplit' + selectedJob.id"
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label class="block text-xs font-medium text-gray-700 mb-1">Client pays ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          [(ngModel)]="billingClientCoveredAmount"
                          [name]="'cliSplit' + selectedJob.id"
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                      </div>
                      <div class="flex flex-col gap-2 md:col-span-2">
                        <label class="inline-flex items-center gap-2 text-sm text-gray-800">
                          <input type="checkbox" [(ngModel)]="billingInsurancePortionBilled" [name]="'insSplitBilled' + selectedJob.id" />
                          Insurance portion billed
                        </label>
                        <label class="inline-flex items-center gap-2 text-sm text-gray-800">
                          <input type="checkbox" [(ngModel)]="billingClientPortionPaid" [name]="'cliSplitPaid' + selectedJob.id" />
                          Client portion paid
                        </label>
                      </div>
                    }
                    @if (billingBillingPaymentMode === JOB_BILLING_PAYMENT_MODE.CashToDriverPayroll) {
                      <div>
                        <label class="block text-xs font-medium text-gray-700 mb-1">Cash collected by driver ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          [(ngModel)]="billingDriverCashCollectedAmount"
                          [name]="'drvCash' + selectedJob.id"
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label class="block text-xs font-medium text-gray-700 mb-1">Payroll deduction ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          [(ngModel)]="billingPayrollDeductionAmount"
                          [name]="'payDed' + selectedJob.id"
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          placeholder="Defaults to cash collected"
                        />
                      </div>
                      <div class="md:col-span-2">
                        <label class="inline-flex items-center gap-2 text-sm text-gray-800">
                          <input type="checkbox" [(ngModel)]="billingPayrollDeductionRecorded" [name]="'payRec' + selectedJob.id" />
                          Record for payroll (deduct from driver)
                        </label>
                      </div>
                    }
                  </div>
                  <div class="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      (click)="saveBillingPayment()"
                      [disabled]="billingSubmitting"
                      class="inline-flex justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      @if (billingSubmitting) {
                        Saving…
                      } @else {
                        Save billing
                      }
                    </button>
                  </div>
                  @if (billingError) {
                    <p class="mt-2 text-sm text-red-600">{{ billingError }}</p>
                  }
                </div>
              }
            </div>

            <!-- Vehicle Information -->
            <div>
              <h3 class="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">Vehicle Information</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                @if (selectedJob.vehicle) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Vehicle</h4>
                    <p class="text-gray-900 font-medium">{{ getVehicleDisplay(selectedJob.vehicle) }}</p>
                    <p class="text-sm text-gray-600 mt-1">VIN: {{ selectedJob.vehicle.vin }}</p>
                  </div>
                }
                @if (selectedJob.licensePlate) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">License Plate</h4>
                    <p class="text-gray-900">{{ selectedJob.licensePlate }} {{ selectedJob.licenseState || '' }}</p>
                  </div>
                }
                @if (selectedJob.vehicleType) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Vehicle Type</h4>
                    <p class="text-gray-900">{{ selectedJob.vehicleType }}</p>
                  </div>
                }
                @if (selectedJob.driveType) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Drive Type</h4>
                    <p class="text-gray-900">{{ selectedJob.driveType }}</p>
                  </div>
                }
                @if (selectedJob.odometer !== null && selectedJob.odometer !== undefined) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Odometer</h4>
                    <p class="text-gray-900">{{ selectedJob.odometer | number }} miles</p>
                  </div>
                }
                @if (selectedJob.drivable) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Drivable</h4>
                    <p class="text-gray-900">{{ selectedJob.drivable }}</p>
                  </div>
                }
                @if (selectedJob.haveKeys !== null && selectedJob.haveKeys !== undefined) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Has Keys</h4>
                    <p class="text-gray-900">{{ selectedJob.haveKeys ? 'Yes' : 'No' }}</p>
                  </div>
                }
                @if (selectedJob.keyLocation) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Key Location</h4>
                    <p class="text-gray-900">{{ selectedJob.keyLocation }}</p>
                  </div>
                }
              </div>
            </div>

            <!-- Client Information -->
            <div>
              <h3 class="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">Client Information</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                @if (selectedJob.clientName) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Client Name</h4>
                    <p class="text-gray-900">{{ selectedJob.clientName }}</p>
                  </div>
                }
                @if (selectedJob.clientEmail) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Client Email</h4>
                    <p class="text-gray-900">{{ selectedJob.clientEmail }}</p>
                  </div>
                }
                @if (selectedJob.clientPhoneNumber) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Client Phone</h4>
                    <p class="text-gray-900">{{ selectedJob.clientPhoneNumber }}</p>
                  </div>
                }
                @if (selectedJob.contactName) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Contact Name</h4>
                    <p class="text-gray-900">{{ selectedJob.contactName }}</p>
                  </div>
                }
                @if (selectedJob.contactPhoneNumber) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Contact Phone</h4>
                    <p class="text-gray-900">{{ selectedJob.contactPhoneNumber }}</p>
                  </div>
                }
              </div>
            </div>

            <!-- Location Information -->
            <div>
              <h3 class="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">Location Information</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                @if (selectedJob.pickupLocation) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Pickup Location</h4>
                    <p class="text-gray-900">{{ selectedJob.pickupLocation }}</p>
                  </div>
                }
                @if (selectedJob.destinationAddress) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Destination Address</h4>
                    <p class="text-gray-900">{{ selectedJob.destinationAddress }}</p>
                  </div>
                }
              </div>
            </div>

            <!-- Company & Assignment -->
            <div>
              <h3 class="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">Company & Assignment</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                @if (selectedJob.companyName) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Company Name</h4>
                    <p class="text-gray-900">{{ selectedJob.companyName }}</p>
                  </div>
                }
                @if (selectedJob.account) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Account</h4>
                    <p class="text-gray-900">{{ selectedJob.account }}</p>
                  </div>
                }
                @if (selectedJob.companyOverride) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Company Override</h4>
                    <p class="text-gray-900">{{ selectedJob.companyOverride }}</p>
                  </div>
                }
                @if (selectedJob.driverName) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Assigned Driver</h4>
                    <p class="text-gray-900">{{ selectedJob.driverName }}</p>
                  </div>
                }
                @if (selectedJob.truckId) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Truck ID</h4>
                    <p class="text-gray-900">{{ selectedJob.truckId }}</p>
                  </div>
                }
                @if (selectedJob.eta) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">ETA</h4>
                    <p class="text-gray-900">{{ formatDate(selectedJob.eta) }}</p>
                  </div>
                }
                @if (selectedJob.scheduledDate) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Scheduled Date</h4>
                    <p class="text-gray-900">{{ formatDate(selectedJob.scheduledDate) }}</p>
                  </div>
                }
                @if (selectedJob.scheduledTime) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Scheduled Time</h4>
                    <p class="text-gray-900">{{ selectedJob.scheduledTime }}</p>
                  </div>
                }
              </div>
            </div>

            <!-- Notes & Additional Information -->
            <div>
              <h3 class="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">Notes & Additional Information</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                @if (selectedJob.notes) {
                  <div class="md:col-span-2">
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Notes</h4>
                    <p class="text-gray-900 whitespace-pre-wrap">{{ selectedJob.notes }}</p>
                  </div>
                }
                @if (selectedJob.billingNotes) {
                  <div class="md:col-span-2">
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Billing Notes</h4>
                    <p class="text-gray-900 whitespace-pre-wrap">{{ selectedJob.billingNotes }}</p>
                    @if (selectedJob.includeBillingNotesOnReceipt) {
                      <p class="text-xs text-gray-500 mt-1">
                        <i class="fas fa-check-circle text-green-600"></i> Included on receipt
                      </p>
                    }
                  </div>
                }
                @if (selectedJob.photoCount !== null && selectedJob.photoCount !== undefined) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Photo Count</h4>
                    <p class="text-gray-900">{{ selectedJob.photoCount }}</p>
                  </div>
                }
              </div>
            </div>

            <!-- Timestamps -->
            <div>
              <h3 class="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">Timestamps</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 class="text-sm font-medium text-gray-500 mb-1">Created At</h4>
                  <p class="text-gray-900">{{ formatDate(selectedJob.createdAt) }}</p>
                </div>
                @if (selectedJob.completedAt) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Completed At</h4>
                    <p class="text-gray-900">{{ formatDate(selectedJob.completedAt) }}</p>
                  </div>
                }
                @if (selectedJob.statusUpdatedAt) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Status Updated At</h4>
                    <p class="text-gray-900">{{ formatDate(selectedJob.statusUpdatedAt) }}</p>
                  </div>
                }
                @if (selectedJob.statusUpdatedByName) {
                  <div>
                    <h4 class="text-sm font-medium text-gray-500 mb-1">Status Updated By</h4>
                    <p class="text-gray-900">{{ selectedJob.statusUpdatedByName }}</p>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  }
</div>

